import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-apollo';
import { BasePage, Helmet, SkeletonLoader, StaticContext } from '@sm/webassets';
import { t, defineMessages } from '@sm/intl';
import { LANGUAGES_BY_CODE } from '@sm/locale';
import { ThemeProvider } from 'react-jss';
import { Box } from '@wds/box';
import GetCollectorById from '~app/helpers/gql/GetCollectorById.graphql';
import GetPollingExperiment from '~app/helpers/gql/GetPollingExperiment.graphql';
import theme from '~app/styles/cmsTheme';
import { pageFunnel, savePollingExperimentCookies } from './utils';
import NonBranded from './NonBranded';
import PollingEndpage from './PollingEndpage';

const COPY = defineMessages({
  DEFAULT_THANKS_MESSAGE: {
    id: 'SurveyThanks.thanksMessage',
    defaultMessage: 'Thank you for taking this survey.',
  },
  META_KEYWORDS: {
    id: 'SurveyThanks.metaKeywords',
    defaultMessage:
      'questionnaire, questionnaires, questionaire, questionaires, free online survey, free online surveys',
  },
  THANKS_PAGE_TITLE: {
    id: 'SurveyThanks.title',
    defaultMessage: 'Thank you! Create Your Own Online Survey Now! | SurveyMonkey',
  },
});

function SurveyThanks(): React.ReactElement {
  const {
    pageOptions: {
      canonical,
      collectorId,
      isBasicEndpageGroup,
      isNonBrandedSmParam = false,
      isWhiteLabel = false,
      isGovernment = false,
      isSmReleaseToggles = false,
      isEmbeddedSmParam = false,
      randomNumber,
      languageId: surveyLangId,
    },
    environment: { countryCode },
    userInfo: { isExistingUser },
  } = useContext(StaticContext);

  const [showFooter, setShowFooter] = useState(false);
  const [toReturn, setToReturn] = useState(
    <Box alignItems="center" display="flex" justifyContent="center" p={5}>
      <SkeletonLoader height="100vh" width={350}>
        <rect x="0" y="50%" rx="8" ry="8" width="350" height="16" />
      </SkeletonLoader>
    </Box>
  );

  const isNonEnglish = parseInt(surveyLangId, 10) !== LANGUAGES_BY_CODE.en.id;
  const country = countryCode.toUpperCase();
  const isUSA = country === 'US';
  const isGAC = country === 'GB' || country === 'AU' || country === 'CA';

  const {
    loading: collectorLoading,
    error: collectorError,
    data: collectorData,
  } = useQuery(GetCollectorById, {
    context: {
      targetPath: '/lo-graphql/collector',
      headers: {
        'X-Collector-Id': collectorId,
        'X-Language-Id': surveyLangId,
      },
    },
    variables: {
      collectorId,
      surveyLangId,
    },
    errorPolicy: 'all',
  });

  const { loading: pollingLoading, error: pollingError, data: pollingData } = useQuery(GetPollingExperiment, {});

  const nonBranded = isNonBrandedSmParam || collectorData?.collector?.hideBranding;
  const isEmbedded = isEmbeddedSmParam || collectorData?.collector?.type?.toUpperCase() === 'POPUP';

  const { Endpage, potentialPollingEndpage } = useMemo(
    () =>
      pageFunnel(
        isSmReleaseToggles,
        isEmbedded,
        isGovernment,
        isWhiteLabel,
        nonBranded,
        isNonEnglish,
        isBasicEndpageGroup,
        isExistingUser,
        isUSA,
        isGAC,
        randomNumber
      ),
    [
      isSmReleaseToggles,
      isEmbedded,
      isGovernment,
      isWhiteLabel,
      nonBranded,
      isNonEnglish,
      isBasicEndpageGroup,
      isExistingUser,
      isUSA,
      isGAC,
      randomNumber,
    ]
  );

  useEffect(() => {
    if (
      potentialPollingEndpage &&
      !collectorLoading &&
      !collectorError &&
      collectorData?.collector &&
      !pollingLoading &&
      !pollingError &&
      pollingData?.getPollingExperiment?.treatmentName &&
      pollingData?.getPollingExperiment?.experimentName
    ) {
      savePollingExperimentCookies(pollingData?.getPollingExperiment?.setCookies);
      setToReturn(
        <PollingEndpage
          defaultEndpage={Endpage}
          pollingExperiment={pollingData?.getPollingExperiment?.experimentName}
          pollingTreatment={pollingData?.getPollingExperiment?.treatmentName}
        />
      );
      setShowFooter(true);
    } else if (!isSmReleaseToggles && (collectorError || !collectorData?.collector)) {
      setToReturn(<NonBranded />);
      setShowFooter(false);
    } else {
      setToReturn(Endpage);
      setShowFooter(!isEmbedded && !nonBranded && !isSmReleaseToggles);
    }
  }, [
    collectorData,
    collectorError,
    nonBranded,
    isEmbedded,
    pollingData,
    pollingError,
    Endpage,
    collectorLoading,
    isSmReleaseToggles,
    isEmbeddedSmParam,
    isNonBrandedSmParam,
    pollingLoading,
    potentialPollingEndpage,
  ]);

  return (
    <ThemeProvider theme={theme}>
      <BasePage includeHeader={false} includeFooter={showFooter} pageId="Survey Thanks" legacyWeb="anonweb">
        <Helmet>
          <base target="_top" href="/" />
          <meta name="keywords" content={t(COPY.META_KEYWORDS)} />
          <meta name="description" content={t(COPY.DEFAULT_THANKS_MESSAGE)} />
          <link rel="canonical" href={canonical} />
          <title>{t(COPY.THANKS_PAGE_TITLE)}</title>
        </Helmet>
        {toReturn}
      </BasePage>
    </ThemeProvider>
  );
}

export default SurveyThanks;
